import { useQuery } from "@tanstack/react-query";
import { Award, TrendingUp, Lock, CircleDashed } from "lucide-react";

import { get } from "@/lib/api-client";
import { formatNumber } from "@/lib/format";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { StatsCard } from "./components/StatsCard";
import { Skeleton } from "@/components/ui/Skeleton";
import type { QualificationSummary } from "@/types/club";

export function QualificationPage() {
  const { data, isLoading } = useQuery({
    queryKey: ["qualification-summary"],
    queryFn: () => get<QualificationSummary>("/qualification/qualification/summary"),
  });

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-bold text-surface-900">Qualification Points</h1>
        <p className="text-sm text-surface-500 mt-1">
          Points move through three states — Pending → Available → Locked. Locked points are permanent.
        </p>
      </header>

      {isLoading ? (
        <div className="grid gap-4 grid-cols-2 lg:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-32 rounded-xl" />)}
        </div>
      ) : data && (
        <>
          <div className="grid gap-4 grid-cols-2 lg:grid-cols-4">
            <StatsCard icon={CircleDashed} label="Pending" tone="warning"
                        value={formatNumber(data.pending)} hint="Under review" />
            <StatsCard icon={Award} label="Available" tone="success"
                        value={formatNumber(data.available)} hint="Ready to lock" />
            <StatsCard icon={Lock} label="Locked" tone="info"
                        value={formatNumber(data.locked)} hint="Consumed by Entries" />
            <StatsCard icon={TrendingUp} label="Total Earned" tone="neutral"
                        value={formatNumber(data.total_earned)} hint={`Reversed: ${formatNumber(data.total_reversed)}`} />
          </div>

          <Card>
            <CardHeader><CardTitle>How qualification works</CardTitle></CardHeader>
            <CardBody className="space-y-4 text-sm text-surface-700 leading-relaxed">
              <div className="flex gap-3">
                <div className="w-8 h-8 rounded-full bg-warning-100 text-warning-700 grid place-items-center font-bold shrink-0">1</div>
                <div>
                  <div className="font-semibold text-surface-900">PENDING</div>
                  <p>Points awaiting admin adjustments or promotional review. Reversible.</p>
                </div>
              </div>
              <div className="flex gap-3">
                <div className="w-8 h-8 rounded-full bg-success-100 text-success-700 grid place-items-center font-bold shrink-0">2</div>
                <div>
                  <div className="font-semibold text-surface-900">AVAILABLE</div>
                  <p>Earned from completed orders. Ready to be consumed to create a Club Entry.
                    Reversible if the underlying order is refunded/cancelled BEFORE consumption.</p>
                </div>
              </div>
              <div className="flex gap-3">
                <div className="w-8 h-8 rounded-full bg-brand-100 text-brand-700 grid place-items-center font-bold shrink-0">3</div>
                <div>
                  <div className="font-semibold text-surface-900">LOCKED</div>
                  <p>Consumed to create a Club Entry. Permanent — never reversed even if a refund occurs later.</p>
                </div>
              </div>
            </CardBody>
          </Card>
        </>
      )}
    </div>
  );
}
